// The canvas's arithmetic, and nothing else. Pure functions over a
// `DashboardLayout`: where a dragged tile lands, what a resize handle does to a
// width or a height, what happens to a row when its last tile leaves. Every
// geometry decision lives here and is unit-tested. The component only reads
// pointer events and paints the answer.
//
// The grid is NOT redefined here. `dashboard-layout.ts` owns the twelve-column
// normalization (`columnSpans`) the renderer uses, so the editor matches the
// dashboard page. This module only decides the stored `width`/`height`.
import type { DashboardLayout, LayoutTile, Schemas } from "@bi/shared/types";

import { GRID_COLUMNS } from "./dashboard-layout";

/** One row of the layout. Aliased locally off `Schemas` rather than imported:
 *  the shared package names the tile union and the layout, but not the row
 *  between them, and this module is the only thing that needs it. */
type LayoutRow = Schemas["LayoutRow"];

/** Narrowest a tile may be dragged. A tile at zero columns is a tile that has
 *  disappeared with no undo affordance and no way to grab it back. */
export const MIN_TILE_WIDTH = 2;

/** Superset's own relative height unit, which `ChartTile.height` is written in.
 *  The renderer ignores it (every primitive has a sane intrinsic size), so these
 *  bounds exist to keep a stored number sane for an EXPORT that a reviewed
 *  Superset dashboard might one day read, not to drive a pixel height. */
export const MIN_TILE_HEIGHT = 20;
export const MAX_TILE_HEIGHT = 200;
export const DEFAULT_TILE_HEIGHT = 50;

/** Height units per pixel of vertical drag. Chosen so a full card height of
 *  drag moves the stored number across roughly its whole useful range: the unit
 *  is relative, so the only thing that matters is that dragging feels
 *  proportional rather than that it maps to a real dimension. */
export const HEIGHT_UNITS_PER_PIXEL = 0.25;

/** Where a tile is: which row, and its index within that row. */
export interface TileAddress {
  row: number;
  index: number;
}

export function sameAddress(a: TileAddress, b: TileAddress): boolean {
  return a.row === b.row && a.index === b.index;
}

export function tileAt(layout: DashboardLayout, address: TileAddress): LayoutTile | undefined {
  return layout.rows[address.row]?.tiles[address.index];
}

function withRows(layout: DashboardLayout, rows: LayoutRow[]): DashboardLayout {
  // Rows that lost their last tile are dropped rather than kept as empty
  // objects: an empty row renders as a silent vertical gap the person who made
  // it cannot see, select, or delete.
  return { ...layout, rows: rows.filter((row) => row.tiles.length > 0) };
}

function cloneRows(layout: DashboardLayout): LayoutRow[] {
  return layout.rows.map((row) => ({ ...row, tiles: [...row.tiles] }));
}

/** Move the tile at `from` to `to` — within its row or into another one.
 *
 *  `to.index` is read as an insertion point in the row AS IT WILL BE after the
 *  tile is lifted out, which is the reading that makes a same-row drag to the
 *  right behave: lifting index 0 shifts everything left by one, so a drop
 *  "after what is now index 1" is index 1, not 2. Getting this backwards is the
 *  classic off-by-one where a tile dragged one place right does not move at
 *  all. */
export function moveTile(
  layout: DashboardLayout,
  from: TileAddress,
  to: TileAddress,
): DashboardLayout {
  const tile = tileAt(layout, from);
  if (!tile) return layout;
  const rows = cloneRows(layout);
  const sourceRow = rows[from.row];
  if (!sourceRow) return layout;
  sourceRow.tiles.splice(from.index, 1);
  const targetRow = rows[to.row];
  if (!targetRow) return layout;
  const index = Math.max(0, Math.min(to.index, targetRow.tiles.length));
  targetRow.tiles.splice(index, 0, tile);
  return withRows(layout, rows);
}

/** Move the tile at `from` into a NEW row inserted at `rowIndex`.
 *
 *  Separate from `moveTile` because "between these two rows" is a different
 *  drop target from "into that row", and collapsing them would make a drop in
 *  the gap between rows land in whichever row the cursor was nearest — the
 *  behaviour that makes a grid editor feel like it is fighting you. */
export function moveTileToNewRow(
  layout: DashboardLayout,
  from: TileAddress,
  rowIndex: number,
): DashboardLayout {
  const tile = tileAt(layout, from);
  if (!tile) return layout;
  const rows = cloneRows(layout);
  const sourceRow = rows[from.row];
  if (!sourceRow) return layout;
  // Insert first, then remove, so the source row's index is still valid while
  // we are computing where the new row goes.
  const at = Math.max(0, Math.min(rowIndex, rows.length));
  rows.splice(at, 0, { tiles: [tile] });
  const shifted = at <= from.row ? from.row + 1 : from.row;
  rows[shifted]?.tiles.splice(from.index, 1);
  return withRows(layout, rows);
}

function clampWidth(width: number): number {
  return Math.max(MIN_TILE_WIDTH, Math.min(GRID_COLUMNS, Math.round(width)));
}

/** The width a resize drag lands on, in grid columns.
 *
 *  `gridWidth` is the pixel width of the whole twelve-column grid, so one
 *  column is `gridWidth / 12` pixels regardless of how the row is currently
 *  divided — the same denominator the renderer uses, which is what makes the
 *  tile follow the cursor instead of drifting away from it on a row whose
 *  widths do not happen to sum to twelve. */
export function widthFromDrag(startWidth: number, deltaX: number, gridWidth: number): number {
  if (gridWidth <= 0) return clampWidth(startWidth);
  return clampWidth(startWidth + (deltaX / gridWidth) * GRID_COLUMNS);
}

/** The height a resize drag lands on, in the layout's own relative units. */
export function heightFromDrag(startHeight: number, deltaY: number): number {
  const next = startHeight + deltaY * HEIGHT_UNITS_PER_PIXEL;
  return Math.max(MIN_TILE_HEIGHT, Math.min(MAX_TILE_HEIGHT, Math.round(next)));
}

/** Replace one tile's stored width, clamped. */
export function setTileWidth(
  layout: DashboardLayout,
  address: TileAddress,
  width: number,
): DashboardLayout {
  return patchTile(layout, address, (tile) => ({ ...tile, width: clampWidth(width) }));
}

/** Replace one tile's stored height, clamped. */
export function setTileHeight(
  layout: DashboardLayout,
  address: TileAddress,
  height: number,
): DashboardLayout {
  return patchTile(layout, address, (tile) => ({
    ...tile,
    height: Math.max(MIN_TILE_HEIGHT, Math.min(MAX_TILE_HEIGHT, Math.round(height))),
  }));
}

function patchTile(
  layout: DashboardLayout,
  address: TileAddress,
  patch: (tile: LayoutTile) => LayoutTile,
): DashboardLayout {
  const tile = tileAt(layout, address);
  if (!tile) return layout;
  const rows = cloneRows(layout);
  const row = rows[address.row];
  if (!row) return layout;
  row.tiles[address.index] = patch(tile);
  return withRows(layout, rows);
}

/** Append a tile as a new bottom row. Where every "add a tile" action lands:
 *  dropping a new tile into an existing row would silently narrow whatever is
 *  already there, and a person who just pressed Add is looking for the new
 *  thing, not for what it did to the row above it. */
export function appendTileRow(layout: DashboardLayout, tile: LayoutTile): DashboardLayout {
  return withRows(layout, [...cloneRows(layout), { tiles: [tile] }]);
}

/** Remove the tile at `address`, dropping its row if it was the last one. */
export function removeTile(layout: DashboardLayout, address: TileAddress): DashboardLayout {
  const rows = cloneRows(layout);
  const row = rows[address.row];
  if (!row || address.index >= row.tiles.length) return layout;
  row.tiles.splice(address.index, 1);
  return withRows(layout, rows);
}

/** The address a selection should move to after `removed` was deleted, or
 *  `null` when the layout is now empty.
 *
 *  Exists because "which tile is selected" survives a delete and a stale
 *  selection is how a property panel ends up editing the tile that took the
 *  deleted one's index. */
export function selectionAfterRemoval(
  layout: DashboardLayout,
  removed: TileAddress,
): TileAddress | null {
  const next = removeTile(layout, removed);
  if (next.rows.length === 0) return null;
  const row = next.rows[Math.min(removed.row, next.rows.length - 1)];
  if (!row || row.tiles.length === 0) return null;
  return {
    row: Math.min(removed.row, next.rows.length - 1),
    index: Math.min(removed.index, row.tiles.length - 1),
  };
}

/** One tile's measured box, in the coordinate space of the canvas element. */
export interface TileRect extends TileAddress {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export type DropTarget =
  /** Insert into an existing row, before the tile at `index`. */
  | { kind: "into"; row: number; index: number }
  /** Open a new row at `row`, pushing everything from there down. */
  | { kind: "newRow"; row: number };

/** How close to a row's top or bottom edge a pointer has to be, in pixels,
 *  before the drop is read as "between the rows" rather than "into this one".
 *  Generous enough to be hittable without aiming, small enough that the middle
 *  of a card is unambiguously an into-the-row drop. */
export const ROW_EDGE_BAND = 14;

/**
 * Where a pointer at `point` would drop a tile, given every tile's measured
 * box.
 *
 * The rule, in order: a pointer inside a row's vertical band drops INTO that
 * row, at the insertion index its horizontal position implies (before the first
 * tile whose horizontal midpoint it has not passed); a pointer within
 * `ROW_EDGE_BAND` of that row's top or bottom edge, or in the gap between two
 * rows, opens a NEW row there; a pointer below every row appends one.
 *
 * Written against measured rectangles rather than against the DOM so it is a
 * function, not an interaction — every case below is a table row in the test
 * rather than something somebody has to reproduce with a mouse.
 */
export function resolveDropTarget(
  rects: readonly TileRect[],
  point: { x: number; y: number },
): DropTarget {
  if (rects.length === 0) return { kind: "newRow", row: 0 };

  const rowIndices = [...new Set(rects.map((r) => r.row))].sort((a, b) => a - b);
  const bands = rowIndices.map((row) => {
    const inRow = rects.filter((r) => r.row === row);
    return {
      row,
      top: Math.min(...inRow.map((r) => r.top)),
      bottom: Math.max(...inRow.map((r) => r.bottom)),
      tiles: inRow.slice().sort((a, b) => a.index - b.index),
    };
  });

  const first = bands[0] as (typeof bands)[number];
  const last = bands[bands.length - 1] as (typeof bands)[number];
  if (point.y < first.top) return { kind: "newRow", row: first.row };
  if (point.y > last.bottom) return { kind: "newRow", row: last.row + 1 };

  for (const band of bands) {
    if (point.y < band.top) {
      // In the gap ABOVE this row and below the previous one.
      return { kind: "newRow", row: band.row };
    }
    if (point.y > band.bottom) continue;
    if (point.y - band.top < ROW_EDGE_BAND) return { kind: "newRow", row: band.row };
    if (band.bottom - point.y < ROW_EDGE_BAND) return { kind: "newRow", row: band.row + 1 };
    let index = band.tiles.length;
    for (const tile of band.tiles) {
      if (point.x < (tile.left + tile.right) / 2) {
        index = tile.index;
        break;
      }
    }
    return { kind: "into", row: band.row, index };
  }
  return { kind: "newRow", row: last.row + 1 };
}

/** Apply a resolved drop target to the layout. The one place the two move
 *  primitives are chosen between, so a caller never has to. */
export function applyDrop(
  layout: DashboardLayout,
  from: TileAddress,
  target: DropTarget,
): DashboardLayout {
  if (target.kind === "newRow") return moveTileToNewRow(layout, from, target.row);
  // A same-row move to a later index is expressed against the row WITHOUT the
  // dragged tile (see `moveTile`), so the index the drop target names — which
  // was computed against the row WITH it — shifts down by one.
  const index =
    target.row === from.row && target.index > from.index ? target.index - 1 : target.index;
  return moveTile(layout, from, { row: target.row, index });
}

/** Where the tile that WAS at `from` ends up after `target` is applied.
 *
 *  The canvas keeps the dragged tile selected across a drop, and computing
 *  "where did it go" by searching the new layout for object identity is fragile
 *  the moment a tile is cloned. Deriving it from the same arithmetic the move
 *  used is not. */
export function addressAfterDrop(
  layout: DashboardLayout,
  from: TileAddress,
  target: DropTarget,
): TileAddress {
  if (target.kind === "newRow") {
    const rowEmptied = (layout.rows[from.row]?.tiles.length ?? 0) === 1;
    // The new row is inserted at `target.row`; if the source row emptied and
    // sat above the insertion point, it is then dropped and everything below
    // it shifts up one.
    const row = rowEmptied && from.row < target.row ? target.row - 1 : target.row;
    return { row, index: 0 };
  }
  const index =
    target.row === from.row && target.index > from.index ? target.index - 1 : target.index;
  const rowEmptied = (layout.rows[from.row]?.tiles.length ?? 0) === 1 && target.row !== from.row;
  const row = rowEmptied && from.row < target.row ? target.row - 1 : target.row;
  return { row, index };
}
